import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { isPushSupported, subscribeToPush, getPushSubscription } from "@/lib/pushClient";
import { isNativeApp } from "@/lib/nativePush";
import { api } from "@/lib/api";

/**
 * One-click "Activer les notifications" button.
 *
 * Push delivery is handled by OneSignal on every platform:
 *   1. **Capacitor iOS / Android** → OneSignal Cordova plugin
 *      (`onesignal-cordova-plugin`). OneSignal registers the device with
 *      APNs/FCM itself; we just call `requestPermission()` and let it bind
 *      the External ID via `OneSignal.login()` (done in AuthContext).
 *   2. **Web / PWA browser** → OneSignal Web SDK + legacy VAPID fallback.
 *
 * On mount it auto-detects the current state and shows:
 *   - "Activer les notifications" if not subscribed
 *   - "Notifications actives" if already subscribed
 *   - "Refusées — autorisez dans les réglages" if permission was denied
 */
function resolveOneSignalNative() {
  if (typeof window === "undefined") return null;
  return window.plugins?.OneSignal || window.cordova?.plugins?.OneSignal || null;
}

async function nativePermissionState() {
  const OS = resolveOneSignalNative();
  if (!OS?.Notifications?.getPermissionAsync) return null;
  try {
    const granted = await OS.Notifications.getPermissionAsync();
    return granted ? "granted" : "prompt";
  } catch (_) {
    return null;
  }
}

export default function EnableNotificationsButton({ className = "", variant = "default", size = "default" }) {
  const [state, setState] = useState("loading"); // loading | off | on | denied | unsupported
  const [busy, setBusy] = useState(false);

  // ── Detect current state ──────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (isNativeApp()) {
      const perm = await nativePermissionState();
      if (perm === "granted") return setState("on");
      if (perm === "prompt") return setState("off");
      // Unknown / SDK not ready → assume off so the user can still tap.
      return setState("off");
    }
    if (!isPushSupported()) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    try {
      const sub = await getPushSubscription();
      setState(sub ? "on" : "off");
    } catch (_) {
      setState("off");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Enable in one click ───────────────────────────────────────────────
  const enable = async () => {
    setBusy(true);
    try {
      if (isNativeApp()) {
        const OS = resolveOneSignalNative();
        if (!OS?.Notifications?.requestPermission) {
          throw new Error("OneSignal SDK indisponible. Réinstallez l'app.");
        }
        const granted = await new Promise((resolve) => {
          try {
            const ret = OS.Notifications.requestPermission(true, (g) => resolve(!!g));
            // Some plugin versions return a Promise directly.
            if (ret && typeof ret.then === "function") ret.then(resolve, () => resolve(false));
          } catch (_) {
            resolve(false);
          }
        });
        if (!granted) {
          setState("denied");
          throw new Error("Notifications refusées. Activez-les dans Réglages > Notifications > Price.");
        }
        setState("on");
        toast.success("Notifications activées", {
          description: "Vous recevrez les alertes Price même app fermée.",
        });
      } else {
        if (!isPushSupported()) {
          throw new Error("Ce navigateur ne supporte pas les notifications push.");
        }
        await subscribeToPush();
        setState("on");
        toast.success("Notifications activées", {
          description: "Vous recevrez les alertes Price même quand l'onglet est fermé.",
        });
      }
    } catch (e) {
      toast.error(e?.message || "Échec de l'activation des notifications");
    } finally {
      setBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (state === "loading") return null;
  if (state === "unsupported") return null;

  if (state === "on") {
    return (
      <div className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
        <div
          className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--success))]/10 px-3 py-1 text-[11.5px] font-medium tracking-tight text-[hsl(var(--success))]"
          data-testid="notifications-active"
        >
          <BellRing className="h-3 w-3" /> Notifications actives
        </div>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <Button
        type="button"
        variant="outline"
        size={size}
        onClick={() => toast.info("Pour réactiver les notifications, ouvrez les réglages de votre appareil → Notifications → Price → Autoriser.")}
        className={`gap-1.5 rounded-full border-[hsl(var(--warning))]/50 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/5 hover:text-[hsl(var(--warning))] ${className}`}
        data-testid="enable-notifications-denied"
      >
        <BellOff className="h-4 w-4" /> Notifications refusées — réactiver
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={enable}
      disabled={busy}
      className={`gap-1.5 rounded-full ${className}`}
      data-testid="enable-notifications"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
      Activer les notifications
    </Button>
  );
}
